import React from 'react';
import { DispatchProp } from 'react-redux';

import {
    characterActions,
    CharacterPreferences,
    Constants,
    HelperUtils,
    HtmlSelectOption,
    RuleData,
    RuleDataUtils,
    rulesEngineSelectors,
    CharacterUtils,
    DataOriginRefData,
    ClassSpellListSpellsLookup,
    ClassUtils,
    CharClass,
    Race,
    RaceUtils,
} from '@dndbeyond/character-rules-engine/es';
import { Dice } from '@dndbeyond/dice';

import { appEnvActions, confirmModalActions } from '../../../../Shared/actions';
import { SimpleClassSpellList } from '../../../../Shared/components/SimpleClassSpellList';
import FormSelectField from '../../../../Shared/components/common/FormSelectField';
import FormToggleField from '../../../../Shared/components/common/FormToggleField';
import FormTogglesField, { ToggleInfo } from '../../../../Shared/components/common/FormTogglesField';
import { appEnvSelectors } from '../../../../Shared/selectors';
import config from '../../../../config';
import Page from '../../../components/Page';
import PageBody from '../../../components/PageBody';
import PageHeader from '../../../components/PageHeader';
import { RouteKey } from '../../../constants/navigation';
import { BuilderAppState } from '../../../typings';
import PortraitName from '../../PortraitName';
import ConnectedBuilderPage from '../ConnectedBuilderPage';

interface Props extends DispatchProp {
    preferences: CharacterPreferences;
    ruleData: RuleData;
    activeSourceCategories: Array<number>;
    diceEnabled: boolean;
    classes: Array<CharClass>;
    race: Race | null;
    dataOriginRefData: DataOriginRefData;
    classSpellListSpellsLookup: ClassSpellListSpellsLookup;
}
class HomeBasicInfo extends React.PureComponent<Props> {
    static defaultProps = {
        diceEnabled: false,
    };

    handlePreferenceChange = (prefKey: string, value: boolean): void => {
        const { dispatch } = this.props;
        const typedPrefKey = CharacterUtils.getPreferenceKey(prefKey);
        if (typedPrefKey !== null) {
            dispatch(characterActions.preferenceChoose(typedPrefKey, value));
        }
    };

    handleIntPreferenceChange = (prefKey: string, value: string): void => {
        const { dispatch } = this.props;
        const typedPrefKey = CharacterUtils.getPreferenceKey(prefKey);
        if (typedPrefKey !== null) {
            dispatch(characterActions.preferenceChoose(typedPrefKey, HelperUtils.parseInputInt(value)));
        }
    };

    handleSourceCategoryChange = (sourceId: number, isActive: boolean): void => {
        const { dispatch, activeSourceCategories } = this.props;

        let newSourceCats: Array<any> = [];
        if (isActive) {
            newSourceCats = [...activeSourceCategories, sourceId];
        } else {
            newSourceCats = activeSourceCategories.filter((id) => id !== sourceId);
        }

        dispatch(characterActions.activeSourceCategoriesSet(newSourceCats));
    };

    handleOptionalClassFeaturesPreferenceChangePromise = (
        newIsEnabled: boolean,
        accept: () => void,
        reject: () => void,
    ): void => {
        const { dispatch, classes, classSpellListSpellsLookup, dataOriginRefData } = this.props;

        const spellListIds: Array<number> = ClassUtils.getUpdateEnableOptionalClassFeaturesSpellListIdsToRemove(
            classes,
            newIsEnabled,
        );

        const hasSpellsToRemove = spellListIds.some((id) => classSpellListSpellsLookup.hasOwnProperty(id));

        if (!hasSpellsToRemove) {
            this.handlePreferenceChange('enableOptionalClassFeatures', newIsEnabled);
            accept();
        } else {
            dispatch(
                confirmModalActions.create({
                    conClsNames: ['confirm-modal-remove'],
                    acceptBtnClsNames: ['character-button-remove'],
                    heading: 'Optional Class Features',
                    content: (
                        <div className="change-preference-content">
                            <p>
                                Are you sure you want to disable <strong>Optional Class Features</strong> for this
                                character?
                            </p>
                            <p>
                                After doing so, the following spells provided by these features will be removed from
                                your character:
                            </p>
                            <SimpleClassSpellList
                                spellListIds={spellListIds}
                                classSpellListSpellsLookup={classSpellListSpellsLookup}
                            />
                        </div>
                    ),
                    onConfirm: () => {
                        this.handlePreferenceChange('enableOptionalClassFeatures', newIsEnabled);
                        accept();
                    },
                    onCancel: () => {
                        reject();
                    },
                }),
            );
        }
    };

    handleOptionalOriginsPreferenceChangePromise = (
        newIsEnabled: boolean,
        accept: () => void,
        reject: () => void,
    ): void => {
        const { dispatch, race, classSpellListSpellsLookup, dataOriginRefData } = this.props;

        if (!race) {
            this.handlePreferenceChange('enableOptionalOrigins', newIsEnabled);
            accept();

            return;
        }

        const spellListIds: Array<number> = RaceUtils.getUpdateEnableOptionalOriginsSpellListIdsToRemove(
            race,
            newIsEnabled,
        );

        const hasSpellsToRemove = spellListIds.some((id) => classSpellListSpellsLookup.hasOwnProperty(id));

        if (!hasSpellsToRemove) {
            this.handlePreferenceChange('enableOptionalOrigins', newIsEnabled);
            accept();
        } else {
            dispatch(
                confirmModalActions.create({
                    conClsNames: ['confirm-modal-remove'],
                    acceptBtnClsNames: ['character-button-remove'],
                    heading: 'Customized Origin Features',
                    content: (
                        <div className="change-preference-content">
                            <p>
                                Are you sure you want to disable <strong>Customized Origins</strong> for this character?
                            </p>
                            <p>
                                After doing so, the following spells provided by these features will be removed from
                                your character:
                            </p>
                            <SimpleClassSpellList
                                spellListIds={spellListIds}
                                classSpellListSpellsLookup={classSpellListSpellsLookup}
                            />
                        </div>
                    ),
                    onConfirm: () => {
                        this.handlePreferenceChange('enableOptionalOrigins', newIsEnabled);
                        accept();
                    },
                    onCancel: () => {
                        reject();
                    },
                }),
            );
        }
    };

    handleProgressionPreferenceChangePromise = (
        newValue: string,
        oldValue: string,
        accept: () => void,
        reject: () => void,
    ): void => {
        const { dispatch } = this.props;

        const prefKey = CharacterUtils.getPreferenceKey('progressionType');
        const newIdValue = HelperUtils.parseInputInt(newValue);
        const oldIdValue = HelperUtils.parseInputInt(oldValue);

        if (newIdValue === Constants.PreferenceProgressionTypeEnum.XP) {
            dispatch(
                confirmModalActions.create({
                    conClsNames: ['confirm-modal-preference'],
                    heading: 'XP Advancement',
                    content: (
                        <div className="change-preference-content">
                            <p>Are you sure you want to change your advancement method to XP progression?</p>
                            <p>You will begin with the base XP value for your current level.</p>
                        </div>
                    ),
                    onConfirm: () => {
                        if (prefKey !== null) {
                            dispatch(characterActions.preferenceChoose(prefKey, newIdValue));
                        }
                        accept();
                    },
                    onCancel: () => {
                        reject();
                    },
                }),
            );
        } else if (newIdValue === Constants.PreferenceProgressionTypeEnum.MILESTONE) {
            dispatch(
                confirmModalActions.create({
                    conClsNames: ['confirm-modal-preference'],
                    heading: 'Milestone Advancement',
                    content: (
                        <div className="change-preference-content">
                            <p>Are you sure you want to change your advancement method to Milestone progression?</p>
                            <p>Your current XP values will be lost.</p>
                        </div>
                    ),
                    onConfirm: () => {
                        if (prefKey !== null) {
                            dispatch(characterActions.preferenceChoose(prefKey, newIdValue));
                        }
                        accept();
                    },
                    onCancel: () => {
                        reject();
                    },
                }),
            );
        }
    };

    handleHitPointPreferenceChangePromise = (
        newValue: string,
        oldValue: string,
        accept: () => void,
        reject: () => void,
    ): void => {
        const { dispatch } = this.props;

        const prefKey = CharacterUtils.getPreferenceKey('hitPointType');
        const newIdValue = HelperUtils.parseInputInt(newValue);
        const oldIdValue = HelperUtils.parseInputInt(oldValue);

        if (newIdValue === Constants.PreferenceHitPointTypeEnum.FIXED) {
            dispatch(
                confirmModalActions.create({
                    conClsNames: ['confirm-modal-preference'],
                    heading: 'Fixed Hit Points',
                    content: (
                        <div className="change-preference-content">
                            <p>Are you sure you want to change your hit points to the fixed value?</p>
                            <p>Any rolled hit point totals will be lost.</p>
                        </div>
                    ),
                    onConfirm: () => {
                        if (prefKey !== null) {
                            dispatch(characterActions.preferenceChoose(prefKey, newIdValue));
                        }
                        accept();
                    },
                    onCancel: () => {
                        reject();
                    },
                }),
            );
        } else if (newIdValue === Constants.PreferenceHitPointTypeEnum.MANUAL) {
            dispatch(
                confirmModalActions.create({
                    conClsNames: ['confirm-modal-preference'],
                    heading: 'Fixed Hit Points',
                    content: (
                        <div className="change-preference-content">
                            <p>Are you sure you want to change your hit points manual entry?</p>
                            <p>After doing so, use Manage HP in the Class section to enter your rolled values.</p>
                        </div>
                    ),
                    onConfirm: () => {
                        if (prefKey !== null) {
                            dispatch(characterActions.preferenceChoose(prefKey, newIdValue));
                        }
                        accept();
                    },
                    onCancel: () => {
                        reject();
                    },
                }),
            );
        }
    };

    handleDiceToggle = (): void => {
        const { dispatch, diceEnabled } = this.props;

        const newDiceEnabledSetting: boolean = !diceEnabled;

        try {
            localStorage.setItem('dice-enabled', newDiceEnabledSetting.toString());
            Dice.setEnabled(newDiceEnabledSetting);
        } catch (e) {}

        dispatch(
            appEnvActions.dataSet({
                diceEnabled: newDiceEnabledSetting,
            }),
        );
    };

    renderPreferences = (): React.ReactNode => {
        const { preferences, ruleData, activeSourceCategories, diceEnabled } = this.props;

        const {
            useHomebrewContent,
            showCompanions,
            encumbranceType,
            hitPointType,
            progressionType,
            abilityScoreDisplayType,
            sharingType,
            privacyType,
            ignoreCoinWeight,
            enforceFeatRules,
            enforceMulticlassRules,
            showScaledSpells,
            enableOptionalOrigins,
            enableOptionalClassFeatures,
        } = preferences;

        const encumbranceOptions: Array<HtmlSelectOption> = [
            { label: 'Use Encumbrance', value: Constants.PreferenceEncumbranceTypeEnum.ENCUMBRANCE },
            { label: 'No Encumbrance', value: Constants.PreferenceEncumbranceTypeEnum.NONE },
            { label: 'Variant Encumbrance', value: Constants.PreferenceEncumbranceTypeEnum.VARIANT },
        ];

        const hpOptions: Array<HtmlSelectOption> = [
            { label: 'Fixed', value: Constants.PreferenceHitPointTypeEnum.FIXED },
            { label: 'Manual', value: Constants.PreferenceHitPointTypeEnum.MANUAL },
        ];

        const advancementOptions: Array<HtmlSelectOption> = [
            { label: 'Milestone', value: Constants.PreferenceProgressionTypeEnum.MILESTONE },
            { label: 'XP', value: Constants.PreferenceProgressionTypeEnum.XP },
        ];

        const abilityDisplayOptions: Array<HtmlSelectOption> = [
            { label: 'Modifiers Top', value: Constants.PreferenceAbilityScoreDisplayTypeEnum.MODIFIERS_TOP },
            { label: 'Scores Top', value: Constants.PreferenceAbilityScoreDisplayTypeEnum.SCORES_TOP },
        ];

        const sharingOptions: Array<HtmlSelectOption> = [
            { label: 'Full', value: Constants.PreferenceSharingTypeEnum.FULL },
            { label: 'Limited', value: Constants.PreferenceSharingTypeEnum.LIMITED },
            // {label: 'Stat Block', value: Constants.PreferenceSharingTypeEnum.STAT_BLOCK},
        ];

        const privacyOptions: Array<HtmlSelectOption> = [
            { label: 'Private', value: Constants.PreferencePrivacyTypeEnum.PRIVATE },
            // {label: 'Campaign Only', value: Constants.PreferencePrivacyTypeEnum.CAMPAIGN_ONLY},
            { label: 'Public', value: Constants.PreferencePrivacyTypeEnum.PUBLIC },
        ];

        let sourceToggles: Array<ToggleInfo> = [];
        let partneredSourceToggles: Array<ToggleInfo> = [];

        RuleDataUtils.getSourceCategories(ruleData).forEach((sourceCategory) => {
            if (!sourceCategory.isToggleable) {
                return null;
            }

            const toggle: ToggleInfo = {
                label: `${sourceCategory.name} Content`,
                initiallyEnabled: activeSourceCategories.includes(sourceCategory.id),
                onChange: this.handleSourceCategoryChange.bind(this, sourceCategory.id),
            };

            if (sourceCategory.isPartneredContent) {
                partneredSourceToggles.push(toggle);
            } else {
                sourceToggles.push(toggle);
            }
        });

        return (
            <div className="home-manage-preferences">
                <FormTogglesField
                    heading="Sources"
                    description="Allow or restrict sources to be used for this character."
                    toggles={[
                        {
                            label: 'Homebrew Content',
                            initiallyEnabled: useHomebrewContent,
                            onChange: this.handlePreferenceChange.bind(this, 'useHomebrewContent'),
                        },
                        ...sourceToggles,
                    ]}
                />
                <FormTogglesField
                    heading="Partnered Content"
                    description="Allow or restrict partnered content to be used for this character. This content is unofficial and should be used only at your DM's discretion."
                    toggles={partneredSourceToggles}
                />
                <FormToggleField
                    heading="Dice Rolling"
                    toggleLabel="Enable Dice Rolling"
                    description="Enables digital dice rolling for this character"
                    initiallyEnabled={diceEnabled}
                    onChange={this.handleDiceToggle}
                />
                <FormTogglesField
                    heading="Optional Features"
                    description="Allow or restrict optional features for this character."
                    toggles={[
                        {
                            label: 'Optional Class Features',
                            initiallyEnabled: enableOptionalClassFeatures,
                            onChangePromise: this.handleOptionalClassFeaturesPreferenceChangePromise,
                        },
                        {
                            label: 'Customize Your Origin',
                            initiallyEnabled: enableOptionalOrigins,
                            onChangePromise: this.handleOptionalOriginsPreferenceChangePromise,
                        },
                    ]}
                />
                <FormSelectField
                    heading="Advancement Type"
                    description="Story-based character progression / XP-based character progression"
                    onChangePromise={this.handleProgressionPreferenceChangePromise}
                    initialOptionRemoved={true}
                    options={advancementOptions}
                    initialValue={'' + progressionType}
                    block={true}
                />
                <FormSelectField
                    heading="Hit Point Type"
                    description="When leveling up, increase hit points by the fixed value for your chosen class or manually enter a rolled value"
                    onChangePromise={this.handleHitPointPreferenceChangePromise}
                    initialOptionRemoved={true}
                    options={hpOptions}
                    initialValue={'' + hitPointType}
                    block={true}
                />
                <FormTogglesField
                    heading="Use Prerequisites"
                    description="Allow or restrict choices based on rule prerequisites for the following for this character"
                    toggles={[
                        {
                            label: 'Feats',
                            initiallyEnabled: enforceFeatRules,
                            onChange: this.handlePreferenceChange.bind(this, 'enforceFeatRules'),
                        },
                        {
                            label: 'Multiclass Requirements',
                            initiallyEnabled: enforceMulticlassRules,
                            onChange: this.handlePreferenceChange.bind(this, 'enforceMulticlassRules'),
                        },
                    ]}
                />
                <FormToggleField
                    heading="Show Level-Scaled Spells"
                    toggleLabel="Show Level-Scaled Spells"
                    description="Display and highlight available spells to cast with higher level spell slots"
                    initiallyEnabled={showScaledSpells}
                    onChange={this.handlePreferenceChange.bind(this, 'showScaledSpells')}
                />
                <FormSelectField
                    heading="Encumbrance Type"
                    description="Use the standard encumbrance rules / Disable the encumbrance display / Use the more detailed rules for encumbrance"
                    onChange={this.handleIntPreferenceChange.bind(this, 'encumbranceType')}
                    initialOptionRemoved={true}
                    options={encumbranceOptions}
                    initialValue={'' + encumbranceType}
                    block={true}
                />
                <FormToggleField
                    heading="Ignore Coin Weight"
                    toggleLabel="Ignore Coin Weight"
                    description="Coins do not count against your total weight carried (50 coins weigh 1 lb.)"
                    initiallyEnabled={ignoreCoinWeight}
                    onChange={this.handlePreferenceChange.bind(this, 'ignoreCoinWeight')}
                />
                <FormSelectField
                    heading="Ability Score/Modifier Display"
                    description="Reverse the arrangement of ability modifiers and scores"
                    onChange={this.handleIntPreferenceChange.bind(this, 'abilityScoreDisplayType')}
                    initialOptionRemoved={true}
                    options={abilityDisplayOptions}
                    initialValue={'' + abilityScoreDisplayType}
                    block={true}
                />
                <FormSelectField
                    heading="Character Privacy"
                    description="Toggle the viewing of your character to 'Private' (only you can access your character), or 'Public' (anyone with the link can view your character)"
                    onChange={this.handleIntPreferenceChange.bind(this, 'privacyType')}
                    initialOptionRemoved={true}
                    options={privacyOptions}
                    initialValue={'' + privacyType}
                    block={true}
                />
                {/*<FormSelectField*/}
                {/*heading='Character Notes Sharing'*/}
                {/*description="Toggle the display of the Notes section for your character to 'Limited' (only you and your campaign DM can see notes), or 'Full' (anyone that can view your character can see notes)"*/}
                {/*onChange={this.handleIntPreferenceChange.bind(this, 'sharingType')}*/}
                {/*initialOptionRemoved={true}*/}
                {/*options={sharingOptions}*/}
                {/*initialValue={sharingType}*/}
                {/*block={true}*/}
                {/*/>*/}
                {/*<FormToggleField*/}
                {/*heading="Show Companions"*/}
                {/*description="Display the Companions section for animals, familiars, and more"*/}
                {/*initiallyEnabled={showCompanions}*/}
                {/*onChange={this.handlePreferenceChange.bind(this, 'showCompanions')}*/}
                {/*/>*/}
            </div>
        );
    };

    render() {
        return (
            <Page clsNames={['home-manage']}>
                <PageBody>
                    <div className="home-manage-primary">
                        <div className="home-manage-primary-section home-manage-primary-section-portrait">
                            <PortraitName />
                        </div>
                    </div>
                    <PageHeader>Character Preferences</PageHeader>
                    {this.renderPreferences()}
                    <div className="home-manage__version">
                        <div className="home-manage__version-label">Version:</div>
                        <div className="home-manage__version-value">{config.version}</div>
                    </div>
                </PageBody>
            </Page>
        );
    }
}

export default ConnectedBuilderPage(HomeBasicInfo, RouteKey.HOME_BASIC_INFO, (state: BuilderAppState) => ({
    preferences: rulesEngineSelectors.getCharacterPreferences(state),
    ruleData: rulesEngineSelectors.getRuleData(state),
    activeSourceCategories: rulesEngineSelectors.getActiveSourceCategories(state),
    diceEnabled: appEnvSelectors.getDiceEnabled(state),
    classes: rulesEngineSelectors.getClasses(state),
    race: rulesEngineSelectors.getRace(state),
    classSpellListSpellsLookup: rulesEngineSelectors.getClassSpellListSpellsLookup(state),
    dataOriginRefData: rulesEngineSelectors.getDataOriginRefData(state),
}));
